//@flow
import React from 'react';

import {
  Typography,
  makeStyles,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core';
import {
  DeleteOutline,
  LocalOfferOutlined,
  // ExpandMore,
  // ChevronRight,
  LocalOffer,
} from '@material-ui/icons';

import Quantity from '../../../elements/Quantity/Quantity';

import {getImageUrl} from '../../../utils/image_provider';

import {commaFormat} from '../../../utils/number';

import {colors} from '../../../assets/styles/colors';

// import DiscountForm from './DiscountForm';
import CustomButton from '../../../elements/CustomButton/CustomButton';

import type {ShoppingCartItem} from '../duck';
import type {DiscountData} from './DiscountForm';
import type {Currency, CartSummaryItemPromotion} from '../../../entities';

type Props = {
  item: ShoppingCartItem,
  taxFactor: number,
  currentStock: number,
  setQuantity: (quantity: number) => void,
  addHandler: () => void,
  removeHandler: () => void,
  discountHandler: (type: DiscountData, value: number) => void,
  productPrice: Object,
  onRemove: () => void,
  currency: Currency,
  promotionsApplied?: CartSummaryItemPromotion[],
  productOptionsNames?: string[],
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
  },
  product: {
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    flexGrow: 1,
  },
  shoppingCartItemsAmount: {
    backgroundColor: colors.grey200,
    borderRadius: '12px',
    padding: '4px 12px',
    fontWeight: 600,
  },
  shoppingCartItems: {
    margin: theme.spacing(0, -3),
    padding: theme.spacing(0, 3),
    maxHeight: 'calc(100vh - 376px)',
    overflow: 'auto',
  },
  itemData: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
  },
  shoppingCartProductDetail: {
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    flexGrow: 1,
  },
  shoppingCartProductImage: {
    borderRadius: '12px',
    height: 32,
    width: 32,
    [theme.breakpoints.down('md')]: {
      height: 48,
      width: 48,
    },
    [theme.breakpoints.up('lg')]: {
      height: 64,
      width: 64,
    },
  },
  shoppingCartProductName: {
    fontSize: '0.875rem',
    display: '-webkit-box',
    overflow: 'hidden',
    lineClamp: 2,
    '-webkit-box-orient': 'vertical',
  },
  shoppingCartProductVariant: {
    fontSize: '0.875rem',
    color: colors.grey700,
    fontWeight: 500,
  },
  shoppingCartProductOptions: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: 500,
  },
  itemActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginTop: '12px',
  },
  shoppingCartItemDeleteButton: {
    color: colors.error,
  },
  shoppingCartProductBulkPriceFlag: {
    padding: 6,
    color: '#158CFD',
    background: '#E9F4FF',
    marginRight: 8,
    display: 'flex',
    borderRadius: '20px',
  },
  shoppingCartProductPrice: {
    textAlign: 'right',
  },
  promotionList: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
    margin: '.25rem 0',
  },
  promotionItem: {
    fontSize: '10px',
    padding: '2px 4px',
    borderRadius: '4px',
    background: `${colors.purple3}33`,
    color: colors.purple3,
    display: 'flex',
    alignItems: 'center',
    maxWidth: '30ch',
  },
  promotionName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    lineClamp: 2,
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
  },
  promotionIcon: {
    fontSize: '10px',
  },
  shoppingCartProductPriceNormal: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    color: colors.grey800,
    whiteSpace: 'nowrap',
  },
  shoppingCartProductPriceDiscounted: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.grey600,
    whiteSpace: 'nowrap',
    textDecoration: 'line-through',
  },
  shoppingCartFooter: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  shoppingCartSubtotal: {
    color: colors.grey800,
  },

  dropDownBtn: {},
  collapse: {
    marginTop: theme.spacing(1),
  },
  notes: {
    margin: theme.spacing(1, 0),
    width: '100%',
    background: colors.grey100,
    border: `.5px solid ${colors.grey500}`,
    borderRadius: '4px',
    padding: '4px 12px',
    fontSize: '13px',
    fontFamily: '"Poppins", "Muli", sans-serif',
    outlineColor: colors.purple,
    '&::placeholder': {
      color: colors.grey500,
    },
  },
  btnAddNote: {
    fontSize: '12px',
    padding: 0,
    color: colors.grey700,
  },
  btnRemove: {
    padding: '.25rem 1rem',
    fontSize: '12px',
    color: colors.error,
  },
  btnDiscount: {
    fontSize: '12px',
    padding: 0,
    color: colors.purple,
  },
}));

export default function ShoppingCardItem(props: Props) {
  const {
    item,
    taxFactor,
    currentStock,
    setQuantity,
    addHandler,
    removeHandler,
    // discountHandler,
    productPrice,
    onRemove,
    currency,
    promotionsApplied = [],
    productOptionsNames = [],
  } = props;

  const classes = useStyles();
  // const largeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const fromMediumScreen = useMediaQuery('(min-width:600px)');

  const imageId = item.productVariant
    ? item.productVariant.imageId
    : item.product.mainImageId;

  return (
    <div className={classes.root}>
      <div className={classes.product}>
        <div className={classes.itemData}>
          <img
            className={classes.shoppingCartProductImage}
            src={getImageUrl(imageId)}
            alt=""
          />
          <div className={classes.shoppingCartProductDetail}>
            <Typography className={classes.shoppingCartProductName}>
              {item.product.name}
            </Typography>
            {item.productVariant && item.productVariant.options && (
              <Typography className={classes.shoppingCartProductVariant}>
                {Object.values(item.productVariant.options).join('/')}
              </Typography>
            )}
            {productOptionsNames.length > 0 && (
              <Typography className={classes.shoppingCartProductOptions}>
                {'• '}
                {productOptionsNames.join(' • ')}
              </Typography>
            )}
            {item.productVariant && item.productVariant.combinations && (
              <Typography className={classes.shoppingCartProductVariant}>
                {Object.values(item.productVariant.combinations)
                  .join('/')
                  .toLowerCase()}
              </Typography>
            )}
            {!!promotionsApplied.length && (
              <div className={classes.promotionList}>
                {promotionsApplied.map(promotion => (
                  <span key={promotion.name} className={classes.promotionItem}>
                    <LocalOffer className={classes.promotionIcon} />
                    <span className={classes.promotionName}>
                      {promotion.name}
                    </span>
                  </span>
                ))}
              </div>
            )}
            {fromMediumScreen && (
              <div
                className={classes.quantityContainer}
                onClick={event => event.stopPropagation()}
              >
                <Quantity
                  value={item.amount}
                  maxValue={
                    item.product.stockSetting &&
                    item.product.stockSetting.enabled &&
                    currentStock > 0
                      ? currentStock
                      : undefined
                  }
                  type={1}
                  setQuantity={setQuantity}
                  addHandler={addHandler}
                  removeHandler={removeHandler}
                  size="sm"
                />
              </div>
            )}
          </div>

          <div className={classes.shoppingCartProductPrice}>
            {fromMediumScreen && (
              <IconButton
                className={classes.shoppingCartItemDeleteButton}
                size="small"
                onClick={event => {
                  event.stopPropagation();
                  onRemove();
                }}
              >
                <DeleteOutline />
              </IconButton>
            )}
            <Typography className={classes.shoppingCartProductPriceNormal}>
              {productPrice && productPrice.source === 'BULK_PRICE' && (
                <Tooltip title="Precio al por mayor aplicado" arrow>
                  <div className={classes.shoppingCartProductBulkPriceFlag}>
                    <LocalOfferOutlined fontSize="small" />
                  </div>
                </Tooltip>
              )}
              {productPrice &&
                item.amount &&
                commaFormat(
                  parseFloat(productPrice.unitPrice) * item.amount * taxFactor,
                  currency ? currency.symbol : ''
                )}
            </Typography>
            {productPrice &&
              parseFloat(productPrice.unitPrice) <
                (item.productVariant
                  ? parseFloat(item.productVariant.price)
                  : parseFloat(item.product.price)) && (
                <Typography
                  className={classes.shoppingCartProductPriceDiscounted}
                >
                  {commaFormat(
                    taxFactor *
                      (item.productVariant
                        ? parseFloat(item.productVariant.price)
                        : parseFloat(item.product.price)) *
                      item.amount,
                    currency ? currency.symbol : ''
                  )}
                </Typography>
              )}
          </div>
        </div>

        {!fromMediumScreen && (
          <div
            className={classes.itemActions}
            onClick={event => event.stopPropagation()}
          >
            <div className={classes.quantityContainer}>
              <Quantity
                value={item.amount}
                maxValue={
                  item.product.stockSetting &&
                  item.product.stockSetting.enabled &&
                  currentStock > 0
                    ? currentStock
                    : undefined
                }
                type={1}
                setQuantity={setQuantity}
                addHandler={addHandler}
                removeHandler={removeHandler}
                size="sm"
              />
            </div>

            {/*  <CustomButton
              className={classes.btnAddNote}
              size="small"
              variant="text"
              onClick={handleNoteCollapse}
            >
              Añadir Nota
            </CustomButton> */}
            <CustomButton
              className={classes.btnRemove}
              size="small"
              variant="text"
              onClick={event => {
                event.stopPropagation();
                onRemove();
              }}
            >
              Eliminar
            </CustomButton>
            {/*   <CustomButton
              className={classes.btnDiscount}
              size="small"
              variant="text"
              onClick={handleDiscountCollapse}
            >
              Descuento %
            </CustomButton> */}
          </div>
        )}

        {/*
        <Hidden smUp>
           <Collapse in={noteIsOpen && !discountOpen}>
            <textarea
              className={classes.notes}
              onInput={event => setNote(event.target.value)}
              placeholder="Notas del pedido"
            />
          </Collapse>
                <Collapse
            className={classes.collapse}
            in={discountOpen && !noteIsOpen}
          >
            <DiscountForm
              maxPrice={productPrice.subTotal}
              onApplyDiscount={discountHandler}
            />
          </Collapse>
        </Hidden>
           */}
      </div>
    </div>
  );
}
